// ─────────────────────────────────────────────────────────────────────────────
// Analytics guide page
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, H3, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideAnalytics() {
  return (
    <GuideLayout
      title="Analytics"
      subtitle="Behavioral patterns across your trading history — what you do, when it works, and when it costs you. 22 detectors organized into four suites, plus performance, calendar, setup, and synthesis views."
      prev={{ slug: "journal",       label: "Journal" }}
      next={{ slug: "eval-survival", label: "Eval Survival" }}
    >
      <H2First>What it's for</H2First>
      <P>
        The other surfaces in Futures OS handle one trade at a time —
        log it, review it, look it up. Analytics is where you look at
        many trades together and ask <em>what's the pattern?</em>
      </P>
      <P>
        The page is organized into 10 sub-tabs. Some show charts (your
        equity curve, monthly P&L, calendar heatmap). The rest run
        detectors — small focused analyses that look at one specific
        behavior across your full trade history and report what they
        find.
      </P>
      <P>
        You're not expected to read all 10 tabs every day. Most users
        check Performance and Calendar daily, then dig into a detector
        suite when a coaching note or AI Insight points them at one.
      </P>

      <ScreenshotFrame
        src="/guide/analytics-overview.png"
        alt="Analytics dashboard with tab row and summary metrics"
        caption="The header strip and tab row stay constant; each tab swaps the content below."
      />

      <H2>The header (every tab)</H2>
      <P>
        At the top of every Analytics tab is a strip of controls and
        summary metrics that don't change as you switch sub-tabs:
      </P>
      <UL>
        <LI><UI>Viewing Account</UI> — switch which account's history you're analyzing</LI>
        <LI><UI>Date Range</UI> — narrow the analysis to a window (default: All Time)</LI>
        <LI><UI>P&L View</UI> — Net (after fees and commissions) or Gross</LI>
        <LI>Six summary cards — Total Net P&L, Win Rate, Profit Factor, Avg Win, Avg Loss, Total Trades — covering the filtered range</LI>
      </UL>
      <P>
        Everything below the header reflects whatever you've selected
        here. Change the date range to "Last 30 days" and every detector
        recomputes for that window.
      </P>

      <H2>Trader Identity Card</H2>
      <P>
        Between the summary metrics and the tab navigation is the
        Trader Identity Card — a synthesis of everything Analytics
        knows about you, compressed into a single card. It appears
        automatically once you have 10 or more logged trades; below
        that threshold it's hidden.
      </P>
      <P>
        The card shows four things, all generated from your actual data:
      </P>
      <UL>
        <LI>
          <strong>Trader type</strong> — a classification like{" "}
          <UI>Precision Trend Follower</UI>,{" "}
          <UI>High-Frequency Scalper</UI>, or{" "}
          <UI>Developing Edge</UI>, derived from your win rate, profit
          factor, setup mix, and trade volume. No two traders see the
          same label — it emerges from your history, not from a quiz.
        </LI>
        <LI>
          <strong>Edge sentence</strong> — your single clearest edge,
          stated directly: your strongest setup by win rate and your
          best time window by win rate, both pulled from your actual
          logged data.
        </LI>
        <LI>
          <strong>Biggest leak</strong> — the highest-cost behavioral
          pattern across all four detector suites, with an estimated
          monthly dollar figure. If no patterns are flagged, a green
          "No costly behavioral patterns detected" pill shows instead.
        </LI>
        <LI>
          <strong>Edge Score</strong> — a 0–100 composite built from
          four components, with a mini breakdown showing exactly how
          each one contributed:
          <UL>
            <LI><UI>Win Rate</UI> — up to 30 points, scaled from 40% to 90%</LI>
            <LI><UI>Profit Factor</UI> — up to 25 points, scaled from 1.0x to 5.0x</LI>
            <LI><UI>Sample Size</UI> — up to 20 points, reaching full at 100+ trades</LI>
            <LI><UI>Clean Patterns</UI> — up to 25 points, reduced by estimated behavioral pattern cost</LI>
          </UL>
        </LI>
      </UL>
      <P>
        Below the score breakdown, a one-line guidance note names the
        weakest component and tells you specifically what to work on —
        for example, "Win rate (58.7%) is your biggest drag. Focus on
        setup quality over trade frequency."
      </P>

      <Callout kind="info" title="The Identity Card can't be configured">
        Everything in the Trader Identity Card is computed from your
        logged trade history. There's nothing to set up — it gets more
        accurate as you log more trades, and changes automatically as
        your trading evolves.
      </Callout>

      <H2>The 10 sub-tabs at a glance</H2>
      <UL>
        <LI><UI>Performance</UI> — equity curve, drawdown, rolling win rate, P&L distribution, monthly P&L</LI>
        <LI><UI>Calendar</UI> — daily P&L laid out month by month, with streak stats</LI>
        <LI><UI>Behavior</UI> — 6 detectors on how you trade (timing, sizing, hold times)</LI>
        <LI><UI>Risk Discipline</UI> — 6 detectors on whether you stick to your rules</LI>
        <LI><UI>Psychology</UI> — 5 detectors on how your mental state affects outcomes</LI>
        <LI><UI>Context</UI> — 4 detectors on where and when you trade well</LI>
        <LI><UI>By Setup</UI> — performance breakdown by setup type</LI>
        <LI><UI>AI Insights</UI> — narrative synthesis of everything else</LI>
        <LI><UI>Discipline</UI> — combined cross-trade view of execution quality</LI>
        <LI><UI>Monthly Review</UI> — month-over-month changes in your patterns</LI>
      </UL>

      <H2>How to read a detector card</H2>
      <P>
        Four of the ten sub-tabs (Behavior, Risk Discipline, Psychology,
        Context) are made up of detector cards. Every card follows the
        same anatomy, so once you can read one you can read all 22.
      </P>

      <ScreenshotFrame
        src="/guide/analytics-detector-card.png"
        alt="Example detector card with annotations"
        caption="Detector card anatomy: name + confidence badge, summary line, finding, sample size, est. cost, expandable details."
      />

      <H3>Top line</H3>
      <UL>
        <LI><strong>Detector name</strong> — what this card analyzes (e.g. <UI>Hour Cliff</UI>)</LI>
        <LI><strong>Confidence badge</strong> — how reliable the read currently is (see below)</LI>
        <LI><strong>One-line subtitle</strong> — what the detector measures, in plain English</LI>
      </UL>

      <H3>The finding</H3>
      <P>
        A specific sentence summarizing what the detector found across
        the trades it analyzed. For example: <em>"Your worst slot is
        19:30 — 15 trades, 7% win rate, net –$355.30."</em> The numbers
        are pulled from your actual logged data.
      </P>

      <H3>Sample size</H3>
      <P>
        Shown as <UI>n = 82</UI> (or similar) on the right side of the
        card. This is how many trades the detector ran across to produce
        the finding. More samples = more confidence.
      </P>
      <P>
        If the sample size is below the threshold for a confident read,
        you'll see <UI>n = 2 / 15 for strong</UI> instead — the
        current sample size and the size needed before the detector
        can claim a strong read.
      </P>

      <H3>Estimated cost</H3>
      <P>
        On Behavior, Psychology, Context, and Risk Discipline cards,
        you'll see <UI>EST. COST / MONTH</UI> with a dollar figure. This
        is an estimate of what the pattern is costing you, extrapolated
        from your observed rate to a monthly figure.
      </P>
      <P>
        Cost figures are estimates surfacing where discipline gaps are
        most expensive — not predictions of future loss. A detector that
        shows <UI>–$121.13/month</UI> isn't promising you'll lose that
        amount next month; it's saying "this pattern, at its current
        rate, would extrapolate to about that."
      </P>

      <H3>Show details</H3>
      <P>
        Most cards have a <UI>▶ Show details</UI> expand at the bottom.
        Clicking it reveals the breakdown — the bucketed data the
        detector used, sometimes a small chart, occasionally a "samples
        needed for confident read" progress bar if the detector is
        still building.
      </P>

      <H3>Three-dot menu</H3>
      <P>
        Most cards have a <UI>⋮</UI> menu in the top right with options
        to acknowledge or dismiss the pattern. Dismissed patterns are
        excluded from the combined cost figure at the top of the suite,
        useful for "yes I know about this, working on it" cases where
        the alert is no longer actionable.
      </P>

      <H2>Confidence levels</H2>
      <P>
        Every detector card has one of four confidence badges. They
        determine how seriously to take the finding.
      </P>
      <UL>
        <LI><strong>STRONG</strong> — enough samples to trust the read. Act on these.</LI>
        <LI><strong>MODERATE</strong> — finding is real but on a smaller sample. Treat as a working hypothesis, not gospel.</LI>
        <LI><strong>THIN</strong> — early signal, often shown with a note like <em>"10 more samples for a confident read."</em> Useful as a tip-off, not a conclusion.</LI>
        <LI><strong>INSUFFICIENT</strong> — not enough data yet to say anything. The card explains what samples it still needs.</LI>
      </UL>

      <Callout kind="tip">
        Confidence levels are the most important single thing to read
        on a detector card. A "your fear-spike trades lose money"
        finding on STRONG (n = 34) is something you can plan around.
        The same finding on INSUFFICIENT (n = 3) is interesting but not
        actionable yet — log more trades with the fear field populated
        and check back.
      </Callout>

      <H2>Performance & Calendar — the non-detector tabs</H2>
      <H3>Performance</H3>
      <P>
        The default view when you open Analytics. Stack of charts:
      </P>
      <UL>
        <LI><UI>Equity Curve</UI> — your account balance over the date range, with a drawdown-from-peak line below</LI>
        <LI><UI>Rolling Win Rate</UI> — your win rate over the most recent 20 trades at every point. Flat = stable edge. Rising = improving. Falling = edge decaying.</LI>
        <LI><UI>P&L Distribution</UI> — histogram of every trade's P&L. Ideal shape: tall bar near your avg win, short bars on large losses. Big outlier loss bars = fat tail risk.</LI>
        <LI><UI>Monthly P&L</UI> — bar chart of each month's net result</LI>
      </UL>
      <P>
        Above the charts, a <UI>Performance Metrics</UI> card aggregates
        expectancy, win/loss ratio, profit factor, max drawdown, largest
        win/loss, monthly consistency, and best month into one row.
      </P>

      <H3>Calendar</H3>
      <P>
        Daily P&L laid out as a month calendar. Each day shows the net
        P&L for that session day and the trade count. Green = winning
        day, red = losing day, color intensity scales with size.
      </P>
      <P>
        Toggles at the top right switch the calendar between displaying
        P&L, trade count, win rate, position size, or discipline score
        per day. Useful for spotting which days of the week or month you
        consistently struggle on — patterns that don't show up in
        rolling averages.
      </P>
      <P>
        A ⚠ icon on a day means at least one trade that day landed in
        your worst time slot (per the Hour Cliff detector).
      </P>
      <P>
        Below the calendar, streak stats: current streak, best win
        streak, worst loss streak, best day, worst day, total trading
        days.
      </P>

      <H2>Behavior suite</H2>
      <P>
        Six detectors on how you actually execute trades.
      </P>
      <UL>
        <LI><UI>Hour Cliff</UI> — time windows where your edge disappears</LI>
        <LI><UI>Day-of-Week Pattern</UI> — which weekdays favor you, which don't</LI>
        <LI><UI>Position Size Tilt</UI> — position size growing after consecutive losses</LI>
        <LI><UI>Win-Streak Euphoria</UI> — position size growing after consecutive wins</LI>
        <LI><UI>Time-in-Trade Tilt</UI> — whether losers are held longer than winners</LI>
        <LI><UI>Revenge Trading</UI> — trades placed shortly after a loss</LI>
      </UL>
      <P>
        At the top of the suite, a <UI>Combined Estimated Cost</UI>{" "}
        figure sums the estimated monthly cost across all firing
        detectors in this suite. Acknowledged and dismissed patterns
        are excluded.
      </P>

      <H2>Risk Discipline suite</H2>
      <P>
        Six detectors on whether you stick to the rules you set for
        yourself. From the page header: <em>"The most actionable
        category of trading data: where you said you'd do one thing and
        did another."</em>
      </P>
      <UL>
        <LI><UI>Overtrading Correlation</UI> — how your light-day P&L compares to heavy-day P&L</LI>
        <LI><UI>Stop Loss Adherence</UI> — how often losing trades exited beyond your planned stop</LI>
        <LI><UI>Position Size Adherence</UI> — whether your sizing stays consistent across trades</LI>
        <LI><UI>Stop Widening</UI> — whether planned stop distances grow during losing streaks</LI>
        <LI><UI>R-Multiple Distribution</UI> — your average winner vs average loser in R-multiple terms</LI>
        <LI><UI>Daily Drawdown Proximity</UI> — how often you trade near your daily drawdown limit</LI>
      </UL>
      <P>
        Detectors that find nothing wrong show a green{" "}
        <UI>disciplined</UI> badge instead of an estimated cost. Those
        are wins worth acknowledging — areas where your execution is
        already clean.
      </P>

      <H2>Psychology suite</H2>
      <P>
        Five detectors on how your mental and emotional state affects
        outcomes. Requires the Reflection-stage fields (fear, focus,
        impulsiveness, conviction, mental state) to be populated.
      </P>
      <UL>
        <LI><UI>Impulsiveness Cost</UI> — performance when you logged high impulsiveness vs disciplined entries</LI>
        <LI><UI>Fear-Spike Trading</UI> — performance when you logged fear ≥7 vs normal conditions</LI>
        <LI><UI>Mental State Edge</UI> — which mental states are net-positive vs negative for your trading</LI>
        <LI><UI>Low Focus Cliff</UI> — performance on sessions you logged as low focus</LI>
        <LI><UI>Conviction Calibration</UI> — whether your gut feeling actually correlates with outcomes</LI>
      </UL>
      <P>
        Below the detector cards, two reference tables: <UI>Emotion-Driven
        Win Rates</UI> (your win rate under high fear, high greed, high
        focus) and <UI>Mental State Breakdown</UI> (your win rate and avg
        P&L for every mental state you've logged).
      </P>

      <Callout kind="info">
        Psychology is the suite that depends most heavily on
        consistent field-logging. A user who only fills in mental state
        on losing trades will see distorted reads — the detector can
        only compare what it has. Best practice: log fear, focus,
        impulsiveness, and conviction on every trade for at least 2-3
        weeks before treating the Psychology reads as definitive.
      </Callout>

      <H2>Context suite</H2>
      <P>
        Four detectors on environmental factors — market conditions,
        news events, exit timing, setup selection.
      </P>
      <UL>
        <LI><UI>Setup Edge Ranking</UI> — which of your setups earn their place in your playbook vs drag on your results</LI>
        <LI><UI>News Event Trading</UI> — your edge on days you flagged as news-driven vs normal conditions</LI>
        <LI><UI>Manual Exit Cliff</UI> — how your manual overrides compare to letting your plan play out</LI>
        <LI><UI>Market Regime Edge</UI> — which market conditions actually favor your trading style</LI>
      </UL>

      <H2>By Setup</H2>
      <P>
        A flat table breaking down every setup type you've used: win
        rate, expectancy, average R-multiple, total P&L, average
        conviction, average impulsiveness. Four featured tiles at the
        top show your most-traded setups at a glance.
      </P>
      <P>
        Useful for the question "which setups should I be taking more
        of, and which should I drop?" — the answer is right in front of
        you.
      </P>

      <H2>AI Insights</H2>
      <P>
        A narrative section that synthesizes findings across all four
        detector suites into a coaching read. Sections include:
      </P>
      <UL>
        <LI><UI>State of Your Trading</UI> — the big-picture summary</LI>
        <LI><UI>Root Cause</UI> — if multiple patterns share a likely root, it's named here</LI>
        <LI><UI>Top Priority This Week</UI> — the single highest-impact pattern, with a suggested rule to add</LI>
        <LI><UI>Pattern Connections</UI> — patterns that likely overlap</LI>
        <LI><UI>What's Working</UI> — strengths to keep doing</LI>
        <LI><UI>Weekly Focus</UI> — a one-line action item for the coming week</LI>
      </UL>
      <P>
        A <UI>Regenerate</UI> button at the top right rebuilds the
        synthesis. The content is informational, not financial advice —
        flagged with a yellow banner at the top.
      </P>

      <H2>Discipline</H2>
      <P>
        A combined cross-trade view of execution quality, summarized
        into three top-level numbers: <UI>Avg Discipline</UI>,{" "}
        <UI>Trend (last 10)</UI>, and <UI>Calm Win Rate</UI>.
      </P>
      <P>
        Below that, the <UI>Key Insights</UI> block surfaces the most
        actionable findings as one-sentence statements, followed by
        reference tables for emotional state win rates, mental state
        breakdown, setup performance, time-of-day breakdown, and
        conviction vs outcome.
      </P>

      <H2>Monthly Review</H2>
      <P>
        Month-over-month changes in your behavioral patterns. Each
        month is its own row showing whether your patterns improved,
        worsened, or stayed stable compared to the previous month, with
        an estimated cost figure for that month. Click into any month
        to see which specific patterns were active.
      </P>
      <P>
        A <UI>Monthly P&L Reference</UI> table at the bottom shows
        trade count, win rate, and net P&L for each month.
      </P>

      <H2>How to actually use Analytics</H2>
      <P>
        Realistic workflow:
      </P>
      <UL>
        <LI><strong>Daily:</strong> glance at the Trader Identity Card — if your Edge Score dropped or a new leak appeared, investigate. Then check Performance + Calendar for ~30 seconds.</LI>
        <LI><strong>Weekly:</strong> AI Insights for the synthesis read. Whatever it names as Top Priority is your focus for the week.</LI>
        <LI><strong>When a detector flags STRONG with a notable cost:</strong> open that suite, expand the card details, see the bucketed data. Decide whether to add a rule, dismiss the pattern, or just watch it.</LI>
        <LI><strong>Monthly:</strong> Monthly Review to see what's improving and what's regressing.</LI>
        <LI><strong>Quarterly:</strong> By Setup. Drop or scale back setups that have run net-negative across enough samples.</LI>
      </UL>
      <P>
        Most users don't need every tab every day. Pick the rhythm
        that fits your trading cadence and let the rest sit until
        something points you at them.
      </P>
    </GuideLayout>
  );
}
